import { useMemo } from 'react';
import { ChevronDown, ChevronRight, Loader2, Minus } from 'lucide-react';
import {
  buildDescendantLeafKeys,
  leafKeys as collectLeafKeys,
  type ProductGroupTreeNode,
} from '@/lib/utils/product-group-tree';
import { useTreeView } from '@/hooks/useTreeView';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface ProductGroupTreeMultiSelectProps {
  /** 商品グループツリー（プレオーダー平坦リスト。配列順=表示順） */
  nodes: ProductGroupTreeNode[];
  /** 選択中の末端ノードのキー */
  selectedLeafKeys: Set<string>;
  onChange: (keys: Set<string>) => void;
  /** チェックボックス id を一意にするための接頭辞（同一画面に複数配置するため） */
  idPrefix: string;
  /** 読み込み中（「該当なし」との誤表示を防ぐ） */
  loading?: boolean;
  /** 選択肢が0件のときの案内文 */
  emptyMessage?: string;
}

/** 1階層ぶんのインデント幅(px)。倉庫ツリーと揃える */
const INDENT_PX = 20;

/** チェックボックスをプロダクトのツリーに合わせて角型にする（WarehouseMultiSelect と同じ） */
const SQUARE_CHECKBOX_CLASS = 'rounded-[2px]';

/**
 * 商品グループの複数選択コンポーネント（階層ツリー表示。FKV3-12941）
 *
 * 倉庫ツリー（`WarehouseMultiSelect`）と同じ操作感に揃える。選択値は末端ノードのキーで持ち、
 * 親のチェックで配下を一括選択/解除する。送信時に「配下がすべて選ばれている親」へ畳むのは
 * フォーム側の責務（`buildSelections`）。
 *
 * 階層の深さはタイプごとに違い、末端の階層も枝によって異なるため、
 * 「何階層目を選ぶか」の指定は持たない（ツリー上で直接選ぶ）。
 */
export function ProductGroupTreeMultiSelect({
  nodes,
  selectedLeafKeys,
  onChange,
  idPrefix,
  loading = false,
  emptyMessage = '商品グループがありません',
}: ProductGroupTreeMultiSelectProps) {
  const viewNodes = useMemo(
    () => nodes.map((n) => ({ name: n.name, level: n.level, isGroup: n.hasChildren })),
    [nodes],
  );
  const {
    search,
    setSearch,
    visibleIndexes,
    hiddenByCollapse,
    collapsedGroups,
    toggleCollapse,
    hasVisibleNodes,
  } = useTreeView(viewNodes);

  const leafCount = useMemo(() => collectLeafKeys(nodes).length, [nodes]);

  // 各親ノード(index)→配下の末端ノードキー
  const descendantLeafKeys = useMemo(() => buildDescendantLeafKeys(nodes), [nodes]);

  // 一括操作・親チェックの対象になる末端キー集合（検索なしなら全件）
  const visibleLeafKeySet = useMemo(() => {
    const set = new Set<string>();
    nodes.forEach((node, index) => {
      if (node.hasChildren) return;
      if (visibleIndexes !== null && !visibleIndexes.has(index)) return;
      set.add(node.key);
    });
    return set;
  }, [nodes, visibleIndexes]);

  const toggleLeaf = (key: string, checked: boolean) => {
    const next = new Set(selectedLeafKeys);
    if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }
    onChange(next);
  };

  // 親の一括トグル: 表示中の配下が全選択済みなら解除、そうでなければ選択（倉庫ツリーと同じ）
  const toggleGroup = (index: number) => {
    const targets = (descendantLeafKeys.get(index) ?? []).filter((key) =>
      visibleLeafKeySet.has(key),
    );
    if (targets.length === 0) return;
    const allSelected = targets.every((key) => selectedLeafKeys.has(key));
    const next = new Set(selectedLeafKeys);
    targets.forEach((key) => (allSelected ? next.delete(key) : next.add(key)));
    onChange(next);
  };

  const groupCheckState = (index: number): boolean | 'indeterminate' => {
    const targets = (descendantLeafKeys.get(index) ?? []).filter((key) =>
      visibleLeafKeySet.has(key),
    );
    if (targets.length === 0) return false;
    const selectedCount = targets.filter((key) => selectedLeafKeys.has(key)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === targets.length) return true;
    return 'indeterminate';
  };

  const checkAllFiltered = () => {
    const next = new Set(selectedLeafKeys);
    visibleLeafKeySet.forEach((key) => next.add(key));
    onChange(next);
  };
  const uncheckAllFiltered = () => {
    const next = new Set(selectedLeafKeys);
    visibleLeafKeySet.forEach((key) => next.delete(key));
    onChange(next);
  };

  if (loading) {
    return (
      <div className="flex items-center rounded-md border p-3 text-sm text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        読み込み中...
      </div>
    );
  }

  if (nodes.length === 0) {
    return <div className="rounded-md border p-3 text-sm text-gray-500">{emptyMessage}</div>;
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="商品グループ名で検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8"
        />
        <Button type="button" variant="outline" size="sm" onClick={checkAllFiltered}>
          すべて選択
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={uncheckAllFiltered}>
          すべて解除
        </Button>
      </div>
      <div className="text-xs text-gray-500">
        選択 {selectedLeafKeys.size} / {leafCount}
      </div>
      <div className="max-h-56 space-y-1 overflow-y-auto">
        {!hasVisibleNodes ? (
          <div className="text-sm text-gray-500">該当する商品グループがありません</div>
        ) : (
          nodes.map((node, index) => {
            if (visibleIndexes !== null && !visibleIndexes.has(index)) return null;
            // 検索中は折りたたみを無視して一致結果を表示する（自動展開扱い）
            if (visibleIndexes === null && hiddenByCollapse.has(index)) return null;
            const indent = { paddingLeft: `${(node.level - 1) * INDENT_PX}px` };
            if (node.hasChildren) {
              const collapsed = collapsedGroups.has(index);
              return (
                <div key={node.key} className="flex items-center gap-2" style={indent}>
                  <button
                    type="button"
                    onClick={() => toggleCollapse(index)}
                    className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-500 hover:text-gray-800"
                    aria-label={collapsed ? '展開' : '折りたたみ'}
                  >
                    {collapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  <Checkbox
                    id={`${idPrefix}-g-${index}`}
                    className={SQUARE_CHECKBOX_CLASS}
                    checked={groupCheckState(index)}
                    onCheckedChange={() => toggleGroup(index)}
                  />
                  <Label htmlFor={`${idPrefix}-g-${index}`} className="font-medium text-gray-700">
                    {node.name}
                  </Label>
                </div>
              );
            }
            return (
              <div key={node.key} className="flex items-center gap-2" style={indent}>
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-300">
                  <Minus className="h-3 w-3" />
                </span>
                <Checkbox
                  id={`${idPrefix}-${node.key}`}
                  className={SQUARE_CHECKBOX_CLASS}
                  checked={selectedLeafKeys.has(node.key)}
                  onCheckedChange={(checked) => toggleLeaf(node.key, checked === true)}
                />
                <Label htmlFor={`${idPrefix}-${node.key}`} className="font-normal">
                  {node.name}
                </Label>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
